/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/B2BEventUsersTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./index.module.css";
import { useTranslation } from "react-i18next";

import { ReactComponent as StarIcon } from "../../assets/icons/B2BEvents/StarIcon.svg";
import { ReactComponent as PauseIcon } from "../../assets/icons/B2BEvents/PauseIcon.svg";
import { ReactComponent as CancelIcon } from "../../assets/icons/B2BEvents/CancelIcon.svg";

/** 1–5 for display, or 0 if invalid (caller should only use when a rating exists). */
const normalizeStarRating = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 1) {
    return 0;
  }
  return Math.min(5, n);
};

/** True when the appointment has a value in the attribute "fechaHoraInicioReal". */
const hasFechaHoraInicioReal = (row) => {
  const v = row?.fechaHoraInicioReal;
  if (v == null) {
    return false;
  }
  if (typeof v === "string") {
    return v.trim().length > 0;
  }
  return true;
};

/**
 * @param {object} props
 * @param {Array<Record<string, unknown>>} props.rows - Virtual line appointments (enriched remitente fields).
 * @param {boolean} props.isColaborador - If true, action column shows "Pausar"; otherwise "Calificar Socio".
 * @param {number} [props.rowNumberOffset=0] - Zero-based offset on the current page (fallback # when no map entry).
 * @param {Map<string, number>} [props.virtualLinePositionById] - 1-based index in the full unfiltered line by `row._id`.
 * @param {(row: Record<string, unknown>) => void} [props.onPauseClick] - Opens pause flow for a virtual line row.
 * @param {(row: Record<string, unknown>) => void} [props.onCancelClick] - Opens cancel-appointment flow for a virtual line row.
 * @param {(row: Record<string, unknown>) => void} [props.onRatePartnerClick] - Opens review appointment flow for a virtual line row.
 */
const B2BEventUsersTable = ({
  rows,
  isColaborador,
  rowNumberOffset = 0,
  virtualLinePositionById,
  onPauseClick,
  onCancelClick,
  onRatePartnerClick,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.indexColumn}>
              {t("pages.b2bEventUsersList.colIndex")}
            </th>
            <th>{t("pages.b2bEventUsersList.colPartnerName")}</th>
            <th>{t("pages.b2bEventUsersList.colEmail")}</th>
            <th>{t("pages.b2bEventUsersList.colCompany")}</th>
            <th>{t("pages.b2bEventUsersList.colActions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const starRating = normalizeStarRating(row.calificacionCita);
            const collaboratorRowCheckedIn =
              isColaborador && hasFechaHoraInicioReal(row);
            const linePosition =
              row._id != null && virtualLinePositionById?.has(String(row._id))
                ? virtualLinePositionById.get(String(row._id))
                : rowNumberOffset + index + 1;
            return (
              <tr
                key={row._id ?? index}
                className={
                  collaboratorRowCheckedIn
                    ? styles.rowAppointmentCheckedIn
                    : undefined
                }
              >
                <td className={styles.indexColumn}>
                  {String(linePosition).padStart(2, "0")}
                </td>
                <td>{row.nombreUsuarioRemitente || "—"}</td>
                <td>{row.correoElectronicoUsuarioRemitente || "—"}</td>
                <td>{row.nombreComercialEmpresaUsuarioRemitente || "—"}</td>
                <td>
                  {isColaborador ? (
                    collaboratorRowCheckedIn ? (
                      <span className={styles.actionsPlaceholder}>—</span>
                    ) : (
                      <div className={styles.actionButtonsGroup}>
                        <button
                          type="button"
                          className={`${styles.iconActionButton} ${styles.iconActionButtonPause}`}
                          title={t("pages.b2bEventUsersList.actionPause")}
                          aria-label={t("pages.b2bEventUsersList.actionPause")}
                          onClick={() => onPauseClick?.(row)}
                        >
                          <PauseIcon className={styles.iconActionButtonSvg} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.iconActionButton} ${styles.iconActionButtonCancel}`}
                          title={t("pages.b2bEventUsersList.actionCancel")}
                          aria-label={t("pages.b2bEventUsersList.actionCancel")}
                          onClick={() => onCancelClick?.(row)}
                        >
                          <CancelIcon className={styles.iconActionButtonSvg} />
                        </button>
                      </div>
                    )
                  ) : row.calificacionCita ? (
                    starRating >= 1 ? (
                      <div
                        className={styles.ratingStarsContainer}
                        role="img"
                        onClick={() => onRatePartnerClick?.(row)}
                        aria-label={t(
                          "pages.b2bEventUsersList.ratingStarsAria",
                          { count: starRating },
                        )}
                      >
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`${styles.ratingStar} ${
                              starRating >= star ? styles.ratingStarFilled : ""
                            }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={styles.actionsPlaceholder}>
                        {String(row.calificacionCita)}
                      </span>
                    )
                  ) : (
                    <button
                      type="button"
                      className={styles.actionButton}
                      title={t("pages.b2bEventUsersList.actionPending")}
                      onClick={() => onRatePartnerClick?.(row)}
                    >
                      <StarIcon className={styles.actionButtonIcon} />
                      <span>
                        {t("pages.b2bEventUsersList.actionRatePartner")}
                      </span>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default B2BEventUsersTable;
